import { Avatar, Dropdown, Input, Layout, Modal, Spin, Tag, Tooltip, message } from 'antd';
import {
  BellOutlined,
  MenuOutlined,
  SearchOutlined,
  SunOutlined,
  MoonOutlined,
  LogoutOutlined,
  UserOutlined,
  SettingOutlined,
  SafetyCertificateOutlined,
  CaretDownOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import { useMemo, useState, useEffect, useCallback, useRef } from 'react';

import { usePanelNavigate } from '../../hooks/usePanelNavigate';

import { useTheme } from '../../contexts/ThemeContext';
import { useAuth } from '../../contexts/AuthContext';
import { canViewTranscriptUi, isPlatformSuperAdmin } from '../../utils/authAccess';
import { canAccessWorkspace } from '../../utils/orgPermissionAccess';
import { isMenuItemVisible } from '../../config/panelMenuAccess';
import { ROUTES } from '../../constants/routes';
import { getInitials } from '../../utils/format';
import { userAvatarUrl } from '../../utils/mapUser';
import { getApiErrorMessage } from '../../utils/apiError';
import {
  buildSearchPageSearch,
  flattenSearchResults,
  getAllowedSearchModelTypes,
  isSearchEmptyApiError,
  normalizeUnifiedSearchResponse,
  resolveSearchCounts,
} from '../../utils/searchResults';
import * as searchApi from '../../services/search.service';
import * as notificationsApi from '../../services/notifications.service';
import NotificationDetailModal from '../notifications/NotificationDetailModal';
import NotificationActionButtons from '../notifications/NotificationActionButtons';
import { openNotificationUrl, sanitizeNotificationBody } from '../../utils/notificationDisplay';
import {
  notificationLayerColor,
  notificationLayerLabel,
  notificationSeverityColor,
  notificationSeverityLabel,
} from '../../constants/notifications';
import QuickSearchDropdown from './QuickSearchDropdown';

import { PANEL_STICKY_HEADER_DEFAULT } from '../../hooks/usePanelStickyOffset';
import '../../styles/header.css';
import '../../styles/search.css';

const QUICK_SEARCH_DEBOUNCE_MS = 300;
const QUICK_SEARCH_LIMIT = 5;

const { Header: AntdHeader } = Layout;

const HeaderIconButton = ({
  icon,
  tooltip,
  onClick,
  variant = 'default',
  active = false,
  className,
  ariaLabel,
  ...rest
}) => {
  const variantCls = {
    default: classNames(
      'text-slate-600 dark:text-slate-300',
      'hover:bg-brand-50 hover:text-brand-600',
      'dark:hover:bg-white/[0.08] dark:hover:text-white',
    ),
    theme: classNames(
      'text-slate-600 dark:text-amber-300',
      'hover:bg-amber-50 hover:text-amber-500',
      'dark:hover:bg-amber-400/15 dark:hover:text-amber-300',
    ),
    notification: classNames(
      'text-slate-600 dark:text-slate-300',
      'hover:bg-rose-50 hover:text-rose-500',
      'dark:hover:bg-rose-500/15 dark:hover:text-rose-300',
    ),
  }[variant];

  return (
    <Tooltip title={tooltip} placement="bottom">
      <button
        type="button"
        onClick={onClick}
        aria-label={ariaLabel || tooltip}
        className={classNames(
          'relative flex h-9 w-9 items-center justify-center rounded-xl',
          'border border-transparent bg-transparent',
          'transition-all duration-200 ease-out',
          'hover:scale-105 active:scale-95',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40',
          variantCls,
          className,
        )}
        {...rest}
      >
        <span className="text-[17px] leading-none">{icon}</span>
        {active && (
          <span className="pointer-events-none absolute right-2 top-2 flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-rose-400 opacity-70" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-white dark:ring-[var(--color-surface)]" />
          </span>
        )}
      </button>
    </Tooltip>
  );
};

const Header = ({ onToggleSidebar, isMobile = false }) => {
  const navigate = usePanelNavigate();
  const location = useLocation();
  const { mode, toggleTheme, isDark } = useTheme();
  const { user, activeOrganization, organizations, logout } = useAuth();
  const accessCtx = useMemo(
    () => ({ user, activeOrganization, organizations }),
    [user, activeOrganization, organizations],
  );
  const showSearch = isMenuItemVisible(ROUTES.SEARCH, accessCtx);
  const platformSuperAdmin = isPlatformSuperAdmin(user);
  const canViewTranscript = canViewTranscriptUi(user);
  const showWorkspaceLink = canAccessWorkspace(user, organizations, activeOrganization);

  const allowedModelTypes = useMemo(
    () => getAllowedSearchModelTypes({
      canViewTranscript,
      includeOrganizations: platformSuperAdmin,
    }),
    [canViewTranscript, platformSuperAdmin],
  );

  const urlSearchQuery = useMemo(() => {
    if (location.pathname !== ROUTES.SEARCH) return '';
    return new URLSearchParams(location.search).get('q') || '';
  }, [location.pathname, location.search]);

  const [searchInput, setSearchInput] = useState('');
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickLoading, setQuickLoading] = useState(false);
  const [quickError, setQuickError] = useState(null);
  const [quickItems, setQuickItems] = useState([]);
  const [quickTotal, setQuickTotal] = useState(0);
  const [notificationItems, setNotificationItems] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const notificationsLoadedRef = useRef(false);
  const quickDebounceRef = useRef(null);
  const quickRequestIdRef = useRef(0);
  const quickWrapRef = useRef(null);

  const loadNotifications = useCallback(async () => {
    if (!user?.id) {
      setNotificationItems([]);
      setUnreadNotifications(0);
      setNotificationsLoading(false);
      return;
    }
    if (!notificationsLoadedRef.current) {
      setNotificationsLoading(true);
    }
    try {
      const result = await notificationsApi.listMyNotifications({ per_page: 8 });
      setNotificationItems(result?.notifications?.data || []);
      setUnreadNotifications(Number(result?.unread_count || 0));
    } catch {
      setNotificationItems([]);
      setUnreadNotifications(0);
    } finally {
      notificationsLoadedRef.current = true;
      setNotificationsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    loadNotifications();
    window.addEventListener('notifications:changed', loadNotifications);
    return () => {
      window.removeEventListener('notifications:changed', loadNotifications);
    };
  }, [loadNotifications, activeOrganization?.id]);

  useEffect(() => {
    if (location.pathname === ROUTES.SEARCH) {
      setSearchInput(urlSearchQuery);
      setQuickOpen(false);
    }
  }, [location.pathname, urlSearchQuery]);

  const closeQuickSearch = useCallback(() => {
    setQuickOpen(false);
  }, []);

  const goToSearchPage = useCallback((q, { sort = 'relevance', mode = 'unified' } = {}) => {
    const trimmed = String(q || '').trim();
    if (trimmed.length < 2) {
      message.warning('حداقل ۲ کاراکتر وارد کنید.');
      return;
    }
    setQuickOpen(false);
    navigate(`${ROUTES.SEARCH}${buildSearchPageSearch({ q: trimmed, mode, sort })}`);
  }, [navigate]);

  const runQuickSearch = useCallback(async (rawQuery) => {
    const trimmed = String(rawQuery || '').trim();
    if (trimmed.length < 2) {
      setQuickItems([]);
      setQuickTotal(0);
      setQuickError(null);
      setQuickLoading(false);
      return;
    }

    const requestId = ++quickRequestIdRef.current;
    setQuickLoading(true);
    setQuickError(null);
    try {
      const res = await searchApi.unifiedSearch({
        query: trimmed,
        sort: 'relevance',
        filters: {
          model_types: allowedModelTypes,
          limit: QUICK_SEARCH_LIMIT,
        },
      });
      if (requestId !== quickRequestIdRef.current) return;
      const normalized = normalizeUnifiedSearchResponse(res);
      setQuickItems(flattenSearchResults(normalized.results, {
        max: QUICK_SEARCH_LIMIT,
        allowedKeys: allowedModelTypes,
      }));
      setQuickTotal(resolveSearchCounts(res, allowedModelTypes).all ?? 0);
    } catch (e) {
      if (requestId !== quickRequestIdRef.current) return;
      setQuickItems([]);
      setQuickTotal(0);
      setQuickError(isSearchEmptyApiError(e) ? null : getApiErrorMessage(e));
    } finally {
      if (requestId === quickRequestIdRef.current) {
        setQuickLoading(false);
      }
    }
  }, [allowedModelTypes]);

  const handleQuickInputChange = useCallback((value) => {
    setSearchInput(value);
    const trimmed = value.trim();
    if (trimmed.length >= 2) {
      setQuickOpen(true);
    } else {
      setQuickOpen(false);
      setQuickItems([]);
      setQuickTotal(0);
      setQuickError(null);
      setQuickLoading(false);
    }

    if (quickDebounceRef.current) clearTimeout(quickDebounceRef.current);
    quickDebounceRef.current = setTimeout(() => {
      runQuickSearch(value);
    }, QUICK_SEARCH_DEBOUNCE_MS);
  }, [runQuickSearch]);

  useEffect(() => () => {
    if (quickDebounceRef.current) clearTimeout(quickDebounceRef.current);
  }, []);

  useEffect(() => {
    if (!quickOpen) return undefined;
    const onDocMouseDown = (event) => {
      if (quickWrapRef.current && !quickWrapRef.current.contains(event.target)) {
        closeQuickSearch();
      }
    };
    const onKeyDown = (event) => {
      if (event.key === 'Escape') closeQuickSearch();
    };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [quickOpen, closeQuickSearch]);

  const submitHeaderSearch = useCallback(() => {
    goToSearchPage(searchInput);
  }, [goToSearchPage, searchInput]);

  const openSearchPage = useCallback(() => {
    const q = searchInput.trim();
    if (q.length >= 2) {
      goToSearchPage(q);
      return;
    }
    navigate(ROUTES.SEARCH);
  }, [navigate, searchInput, goToSearchPage]);

  const displayName = user?.name || 'کاربر مهمان';
  const displayMeta = 'حساب کاربری';

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'پروفایل من',
      onClick: () => navigate(ROUTES.PROFILE),
    },
    {
      key: 'security',
      icon: <SafetyCertificateOutlined />,
      label: 'امنیت حساب',
      onClick: () => navigate(ROUTES.SECURITY),
    },
  ];
  if (showWorkspaceLink) {
    userMenuItems.push({
      key: 'workspace',
      icon: <SettingOutlined />,
      label: 'سازمان من',
      onClick: () => navigate(ROUTES.WORKSPACE),
    });
  }
  if (platformSuperAdmin) {
    userMenuItems.push({
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'تنظیمات حساب',
      onClick: () => navigate(ROUTES.SETTINGS),
    });
  }
  userMenuItems.push(
    { type: 'divider' },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'خروج از حساب',
      danger: true,
      onClick: () => {
        logout();
        navigate(ROUTES.LOGIN);
      },
    },
  );

  const userMenu = { items: userMenuItems };

  const openNotification = async (notification) => {
    const opened = { ...notification, is_read: true, read_at: notification.read_at || new Date().toISOString() };
    setSelectedNotification(opened);
    if (!notification.is_read) {
      setNotificationItems((items) => items.map((item) => (
        item.id === notification.id ? opened : item
      )));
      setUnreadNotifications((count) => Math.max(0, count - 1));
      try {
        await notificationsApi.markNotificationRead(notification.id);
        window.dispatchEvent(new CustomEvent('notifications:changed'));
      } catch {
        loadNotifications();
      }
    }
  };

  const readAllNotifications = async () => {
    setNotificationItems((items) => items.map((item) => ({ ...item, is_read: true })));
    setUnreadNotifications(0);
    try {
      await notificationsApi.markAllNotificationsRead();
      window.dispatchEvent(new CustomEvent('notifications:changed'));
    } catch {
      loadNotifications();
    }
  };

  const clearAllNotifications = () => {
    Modal.confirm({
      title: 'همه اعلان‌ها پاک شوند؟',
      content: 'فهرست اعلان‌های شما خالی می‌شود.',
      okText: 'پاک کردن همه',
      cancelText: 'انصراف',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await notificationsApi.clearAllNotifications();
          setNotificationItems([]);
          setUnreadNotifications(0);
          setSelectedNotification(null);
          window.dispatchEvent(new CustomEvent('notifications:changed'));
          message.success('همه اعلان‌ها پاک شدند.');
        } catch {
          message.error('پاک کردن اعلان‌ها انجام نشد.');
          loadNotifications();
        }
      },
    });
  };

  const openNotificationAction = (url) => {
    setSelectedNotification(null);
    openNotificationUrl(url, navigate);
  };

  const notificationMenuItems = [
    {
      key: 'notification-heading',
      disabled: true,
      label: (
        <div className="flex items-center justify-between gap-6 py-1">
          <span className="font-semibold text-slate-800 dark:text-slate-100">اعلان‌ها</span>
          <span className="text-xs text-slate-500 dark:text-slate-400">
            {unreadNotifications ? `${unreadNotifications.toLocaleString('fa-IR')} خوانده‌نشده` : 'همه خوانده شده'}
          </span>
        </div>
      ),
    },
    { type: 'divider' },
    ...(notificationsLoading
      ? [{ key: 'notification-loading', disabled: true, label: <div className="flex justify-center py-5"><Spin size="small" /></div> }]
      : notificationItems.length
      ? notificationItems.map((notification) => ({
        key: notification.id,
        onClick: () => openNotification(notification),
        label: (
          <div className="flex max-w-[360px] gap-2 py-1.5">
            <span className={classNames(
              'mt-1.5 h-2 w-2 shrink-0 rounded-full',
              notification.is_read ? 'bg-slate-300 dark:bg-slate-600' : 'bg-brand-500',
            )} />
            <span className="flex min-w-0 flex-col gap-1">
              <span className={classNames(
                'truncate text-sm text-slate-800 dark:text-slate-100',
                !notification.is_read && 'font-semibold',
              )}>
                {notification.subject || 'اعلان جدید'}
              </span>
              <span className="flex flex-wrap gap-1">
                <Tag color={notificationLayerColor(notification.layer)} className="!m-0 !text-[10px]">
                  {notificationLayerLabel(notification.layer)}
                </Tag>
                <Tag color={notificationSeverityColor(notification.severity)} className="!m-0 !text-[10px]">
                  {notificationSeverityLabel(notification.severity)}
                </Tag>
              </span>
              <span className="line-clamp-2 text-xs text-slate-500 dark:text-slate-400">{sanitizeNotificationBody(notification.body)}</span>
              <NotificationActionButtons
                className="mt-1"
                notification={notification}
                navigate={navigate}
              />
              {notification.created_at && (
                <span className="text-[11px] text-slate-400">
                  {new Date(notification.created_at).toLocaleString('fa-IR')}
                </span>
              )}
            </span>
          </div>
        ),
      }))
      : [{ key: 'notification-empty', disabled: true, label: <div className="py-5 text-center text-sm text-slate-500">اعلانی ثبت نشده است.</div> }]),
    { type: 'divider' },
    {
      key: 'notification-actions',
      label: (
        <div className="flex items-center justify-center gap-4">
          <button type="button" className="text-brand-600" onClick={readAllNotifications} disabled={!unreadNotifications}>
            خواندن همه
          </button>
          <button type="button" className="inline-flex items-center gap-1 text-rose-600" onClick={clearAllNotifications} disabled={!notificationItems.length}>
            <DeleteOutlined />
            پاک کردن همه
          </button>
        </div>
      ),
    },
    {
      key: 'notification-show-all',
      label: <div className="text-center font-medium text-brand-600">مشاهده همه اعلان‌ها</div>,
      onClick: () => navigate(ROUTES.NOTIFICATIONS),
    },
  ];

  return (
    <>
      <AntdHeader
        className={classNames(
          'sticky top-0 z-30 app-header !flex !items-center !justify-between',
          '!border-b !border-[var(--color-border)]',
          '!bg-[var(--color-surface)]/95',
          '!shadow-[var(--shadow-soft)]',
          '!backdrop-blur-xl',
          '!px-3 sm:!px-5',
        )}
        style={{ height: PANEL_STICKY_HEADER_DEFAULT }}
      >
      <div className="flex items-center gap-2 sm:gap-3">
        {isMobile && (
          <HeaderIconButton icon={<MenuOutlined />} tooltip="منو" onClick={onToggleSidebar} />
        )}

        {showSearch && (
          <div className="relative hidden md:block" ref={quickWrapRef}>
            <Input
              allowClear
              value={searchInput}
              onChange={(e) => handleQuickInputChange(e.target.value)}
              onFocus={() => {
                if (searchInput.trim().length >= 2) setQuickOpen(true);
              }}
              onBlur={() => {
                window.setTimeout(() => closeQuickSearch(), 150);
              }}
              onPressEnter={submitHeaderSearch}
              onKeyDown={(e) => {
                if (e.key === 'Escape') {
                  e.stopPropagation();
                  closeQuickSearch();
                }
              }}
              prefix={<SearchOutlined className="text-slate-400 dark:text-slate-500" />}
              placeholder={
                canViewTranscript
                  ? 'جستجو در جلسات، اتاق‌ها، صورت‌جلسات و رونوشت…'
                  : 'جستجو در جلسات، اتاق‌ها و صورت‌جلسات…'
              }
              className={classNames(
                '!h-10 !w-[280px] !rounded-xl text-right placeholder:text-right',
                '!border-[var(--color-border)] !bg-[var(--color-surface-2)]',
                'hover:!border-[var(--color-brand)] focus-within:!border-[var(--color-brand)]',
                'lg:!w-[380px] xl:!w-[440px]',
              )}
            />
            <QuickSearchDropdown
              open={quickOpen}
              loading={quickLoading}
              error={quickError}
              query={searchInput}
              items={quickItems}
              totalCount={quickTotal}
              onViewAll={() => goToSearchPage(searchInput)}
              onSelectResult={closeQuickSearch}
            />
          </div>
        )}
      </div>

      <div className="flex items-center gap-1 sm:gap-1.5">
        {showSearch && (
          <HeaderIconButton
            icon={<SearchOutlined />}
            tooltip="جستجو"
            className="md:!hidden"
            onClick={openSearchPage}
          />
        )}

        <HeaderIconButton
          icon={isDark ? <SunOutlined /> : <MoonOutlined />}
          onClick={toggleTheme}
          variant="theme"
          ariaLabel={`تغییر تم به ${mode === 'light' ? 'تاریک' : 'روشن'}`}
        />

        <Dropdown
          menu={{ items: notificationMenuItems }}
          trigger={['click']}
          placement="bottomLeft"
          arrow
          overlayStyle={{ minWidth: 320 }}
          onOpenChange={(open) => {
            if (open) loadNotifications();
          }}
        >
          <div>
            <HeaderIconButton
              icon={<BellOutlined />}
              variant="notification"
              active={unreadNotifications > 0}
              tooltip="اعلان‌ها"
            />
          </div>
        </Dropdown>

        <div className="mx-1 hidden h-6 w-px bg-[var(--color-border)] sm:block" />

        <div className="relative inline-flex">
          <Dropdown
            menu={userMenu}
            trigger={['click']}
            placement="bottomLeft"
            arrow={{ pointAtCenter: true }}
            classNames={{ root: 'header-user-menu-overlay' }}
          >
            <button type="button" className="header-user-chip" aria-label="منوی حساب کاربری">
              <span className="header-user-chip__avatar-wrap">
                <Avatar
                  size={36}
                  src={userAvatarUrl(user) || undefined}
                  className="!bg-brand-gradient !font-semibold !text-white"
                >
                  {getInitials(displayName)}
                </Avatar>
                <span className="header-user-chip__status" aria-hidden />
              </span>
              <span className="header-user-chip__text">
                <span className="header-user-chip__meta">{displayMeta}</span>
                <span className="header-user-chip__name">{displayName}</span>
              </span>
              <CaretDownOutlined className="header-user-chip__chevron" />
            </button>
          </Dropdown>
        </div>
      </div>
      </AntdHeader>
      <NotificationDetailModal
        open={Boolean(selectedNotification)}
        notification={selectedNotification}
        onClose={() => setSelectedNotification(null)}
        onOpenAction={openNotificationAction}
        navigate={navigate}
      />
    </>
  );
};

export default Header;
